import { ChangeDetectionStrategy, Component, ElementRef, input, output, ViewChild } from '@angular/core';
import { ErrorHandlerService } from '@examdojo/error-handling';
import { ToastService } from '@examdojo/toast';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { finalize, first, of, tap } from 'rxjs';
import { ImageStorageHttpService } from '../../storage/image-storage-http.service';
import { ImageCropDialogService } from '../dialog/image-crop-dialog.service';

@UntilDestroy()
@Component({
  selector: 'dojo-image-crop-file-input',
  templateUrl: './image-crop-file-input.component.html',
  styleUrls: ['./image-crop-file-input.component.scss'],
  imports: [],
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [ImageCropDialogService, ImageStorageHttpService],
})
export class ImageCropFileInputComponent {
  constructor(
    private readonly imageCropDialogService: ImageCropDialogService,
    private readonly imageStorageHttpService: ImageStorageHttpService,
    private readonly toastService: ToastService,
    private readonly errorHandlerService: ErrorHandlerService,
  ) {}

  readonly maxFileSize = input<number>(Infinity);

  readonly uploaded = output<string>();
  readonly storageName = input.required<string>();

  @ViewChild('fileInput') fileInput!: ElementRef<HTMLInputElement>;

  async onImageSelected(event: Event) {
    const file = (event.target as HTMLInputElement).files?.[0];

    if (!file) {
      return;
    }

    const imageBlob = await this.imageCropDialogService.openDialog({
      options: {
        componentProps: {
          file,

          // TODO: Make it configurable
          options: {
            enableContain: false,
            enableRatio: false,
            enableFlip: false,
            enablePreview: false,
            enableRotate: false,
            enableZoom: false,
            ratioOptions: [{ key: '1:1', value: 1 }],
            initRatio: 1,
            lockRatio: true,
          },
        },
      },
    });

    if (!imageBlob) {
      return;
    }

    this.uploadImage(file.name, imageBlob)
      .pipe(
        first(),
        finalize(() => {
          this.fileInput.nativeElement.value = '';
        }),
        untilDestroyed(this),
      )
      .subscribe();
  }

  openFilePicker() {
    this.fileInput.nativeElement.click();
  }

  private uploadImage(fileName: string, blob: Blob) {
    if (blob.size > this.maxFileSize()) {
      this.toastService.error(
        `Image size should not exceed ${Math.floor(this.maxFileSize() / 1024)} KB. Got ${Math.floor(
          blob.size / 1024,
        )} KB`,
      );
      return of(undefined);
    }

    return this.imageStorageHttpService.uploadImage(this.storageName(), fileName, blob).pipe(
      tap((img) => {
        this.uploaded.emit(img.url);
        this.toastService.success('Image uploaded successfully');
      }),
      this.errorHandlerService.catchError(
        '[ImageCropFileInputComponent.uploadImage]: could not upload the image',
        () => of(undefined),
        {
          toast: 'Could not upload the image',
        },
      ),
    );
  }
}
